import { Github, Linkedin, Mail, Terminal, Code2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useEffect, useRef, useState, useMemo } from "react";
import { gsap } from "gsap";
import ThreeBackground from "./Backgrounds/HeroBackground";
import { useTheme } from "@/theme/ThemeContext";
import { theme } from "@/theme/theme";
import { useToast } from "@/hooks/use-toast";

const Hero = () => {
  const imageRef = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const buttonsRef = useRef<HTMLDivElement>(null);
  const socialRef = useRef<HTMLDivElement>(null);
  const { theme: themeMode } = useTheme();
  const { toast } = useToast();
  const [isFlipping, setIsFlipping] = useState(false);
  const [displayTheme, setDisplayTheme] = useState(themeMode);
  const [flipDirection, setFlipDirection] = useState(0);
  const [prevImage, setPrevImage] = useState(
    themeMode === "dark" ? "/images/red-image.jpg" : "/images/bw-image.jpg"
  );

  // Memoize theme styles
  const commonStyles = useMemo(() => theme.common[themeMode], [themeMode]);
  const heroStyles = useMemo(() => theme.hero[themeMode], [themeMode]);
  const displayCommonStyles = useMemo(
    () => theme.common[displayTheme],
    [displayTheme]
  );
  const displayHeroStyles = useMemo(
    () => theme.hero[displayTheme],
    [displayTheme]
  );

  // Handle theme changes
  useEffect(() => {
    if (imageRef.current) {
      setIsFlipping(true);
      setFlipDirection((prev) => prev + 1);

      const flipTimer = setTimeout(() => {
        setDisplayTheme(themeMode);
        const resetTimer = setTimeout(() => setIsFlipping(false), 100);
        return () => clearTimeout(resetTimer);
      }, 600);

      return () => clearTimeout(flipTimer);
    }
  }, [themeMode]);

  // Update previous image
  useEffect(() => {
    setPrevImage(
      displayTheme === "dark" ? "/images/red-image.jpg" : "/images/bw-image.jpg"
    );
  }, [displayTheme]);

  // Initialize animations
  useEffect(() => {
    const tl = gsap.timeline({ delay: 0.5 });

    // Image animation
    tl.fromTo(
      imageRef.current,
      { scale: 0.8, opacity: 0, y: 50 },
      { scale: 1, opacity: 1, y: 0, duration: 1, ease: "back.out(1.7)" }
    );

    // Description animation
    tl.fromTo(
      descriptionRef.current,
      { x: -50, opacity: 0, rotateY: -30, transformOrigin: "left center" },
      { x: 0, opacity: 1, rotateY: 0, duration: 0.8, ease: "power2.out" },
      "-=0.4"
    );

    // Buttons animation
    tl.fromTo(
      buttonsRef.current,
      { x: -30, opacity: 0, scale: 0.9 },
      { x: 0, opacity: 1, scale: 1, duration: 0.6, ease: "back.out(1.7)" },
      "-=0.3"
    );

    // Social icons animation
    tl.fromTo(
      socialRef.current?.children,
      { y: 20, opacity: 0, scale: 0, rotate: -15 },
      {
        y: 0,
        opacity: 1,
        scale: 1,
        rotate: 0,
        duration: 0.5,
        stagger: 0.1,
        ease: "back.out(1.7)",
      },
      "-=0.2"
    );

    // Floating animation
    gsap.to(imageRef.current, {
      y: -10,
      duration: 2,
      repeat: -1,
      yoyo: true,
      ease: "power2.inOut",
    });
  }, []);

  const profileImage =
    displayTheme === "dark" ? "/images/red-image.jpg" : "/images/bw-image.jpg";

  return (
    <section
      id="home"
      className="min-h-screen flex items-center justify-center px-2 sm:px-6 lg:px-8 pt-12 md:pt-16 pb-16 md:pb-0 mb-16 md:mb-0 relative overflow-hidden"
    >
      <div className="absolute inset-0 z-0">
        <ThreeBackground />
      </div>

      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center relative z-10">
        {/* Profile Image Section */}
        <div className="relative z-10">
          <div
            ref={imageRef}
            className="relative mx-auto flex flex-col items-center w-full aspect-square max-w-[220px] md:max-w-md animate-float"
            style={{ perspective: "1000px", transformStyle: "preserve-3d" }}
          >
            <div
              className="relative w-full h-full"
              style={{
                transformStyle: "preserve-3d",
                transition: "transform 1.2s cubic-bezier(0.4, 0, 0.2, 1)",
                transform: `rotateY(${180 * flipDirection}deg)`,
              }}
            >
              {/* Front and Back Faces */}
              {[0, 180].map((rotation) => (
                <div
                  key={rotation}
                  className="absolute w-full h-full"
                  style={{
                    backfaceVisibility: "hidden",
                    transform: `rotateY(${rotation}deg)`,
                  }}
                >
                  <div
                    className={`absolute inset-0 p-1 rounded-full bg-gradient-to-tr ${displayCommonStyles.border.gradient} animate-spin-slow`}
                    style={{ filter: "blur(2.5px)" }}
                  />
                  <div className="absolute inset-4 rounded-full bg-black/10 blur-2xl" />
                  <div
                    className={`relative w-full h-full rounded-full overflow-hidden border-4 ring-4 border-transparent ${commonStyles.ring.glow} ${commonStyles.shadow.glow} transition-all duration-500`}
                  >
                    <div className="relative w-full h-full">
                      <img
                        src={prevImage}
                        alt="Adarsh Parmar"
                        className={`absolute inset-0 w-full h-full object-cover rounded-full scale-105 transition-opacity duration-500 ${
                          isFlipping ? "opacity-0" : "opacity-100"
                        }`}
                      />
                      <img
                        src={profileImage}
                        alt="Adarsh Parmar"
                        className={`absolute inset-0 w-full h-full object-cover rounded-full scale-105 transition-opacity duration-500 ${
                          isFlipping ? "opacity-100" : "opacity-0"
                        }`}
                      />
                    </div>
                  </div>

                  {/* Badge Section */}
                  <div
                    className={`absolute -bottom-8 left-1/2 -translate-x-1/2 px-4 py-2 md:px-8 md:py-3 rounded-full min-w-[120px] md:min-w-[160px] flex-shrink-0 ${commonStyles.background.glass} shadow-lg flex flex-col items-center gap-1 transition-all duration-500 hidden md:flex w-max`}
                  >
                    <span
                      className={`font-mono text-sm md:text-lg font-bold ${displayHeroStyles.badge.text} tracking-widest drop-shadow`}
                    >
                      &lt; Full Stack Developer &gt;
                    </span>
                  </div>

                  <div
                    className={`absolute -bottom-6 left-1/2 -translate-x-1/2 px-5 py-2 rounded-full min-w-[140px] flex-shrink-0 ${commonStyles.background.glass} shadow-lg flex flex-col items-center gap-1 transition-all duration-500 md:hidden w-max`}
                  >
                    <span
                      className={`font-mono text-xs font-bold ${displayHeroStyles.badge.text} tracking-wider drop-shadow px-1`}
                    >
                      &lt; Full Stack Developer &gt;
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="text-left relative px-2 md:px-0 mt-24 md:mt-0">
          <div className="mb-6 md:mb-8">
            <div className="relative z-10 mb-6 md:mb-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="relative">
                  <div
                    className="absolute -inset-3 rounded-full pointer-events-none"
                    style={{
                      zIndex: 0,
                      background:
                        themeMode === "dark"
                          ? "radial-gradient(circle, rgba(255,140,0,0.25) 0%, rgba(255,140,0,0.0) 80%)"
                          : "radial-gradient(circle, rgba(255,255,255,0.7) 40%, rgba(255,255,255,0.0) 80%)",
                    }}
                  />
                  <Terminal
                    className={`w-8 h-8 ${commonStyles.text.accent} relative z-10`}
                  />
                </div>
                <h2
                  className={`text-2xl font-mono ${commonStyles.text.accent}`}
                >
                  Welcome to my portfolio
                </h2>
              </div>

              <p
                ref={descriptionRef}
                className={`max-w-xs md:max-w-2xl font-mono relative z-10 mb-6 md:mb-8 px-3 py-3 md:px-6 md:py-4 rounded-xl border transition-all duration-300 shadow-md ${heroStyles.description.background}`}
                style={{ fontSize: "1rem", lineHeight: "1.6" }}
              >
                <span className={heroStyles.code.const}>const</span>{" "}
                <span className={heroStyles.code.var}>developer</span>{" "}
                <span className={heroStyles.code.equals}>=</span>{" "}
                <span className={heroStyles.code.brace}>{"{"}</span>
                <br />
                &nbsp;&nbsp;<span className={heroStyles.code.var}>
                  name
                </span>:{" "}
                <span className={heroStyles.code.string}>"Adarsh Parmar"</span>,
                <br />
                &nbsp;&nbsp;<span className={heroStyles.code.var}>skills</span>:
                [<span className={heroStyles.code.value}>"React"</span>,{" "}
                <span className={heroStyles.code.value}>"Vue"</span>,{" "}
                <span className={heroStyles.code.value}>"Node.js"</span>],
                <br />
                &nbsp;&nbsp;<span className={heroStyles.code.var}>
                  focus
                </span>:{" "}
                <span className={heroStyles.code.value}>
                  "Building scalable web applications"
                </span>
                <br />
                <span className={heroStyles.code.brace}>{"}"}</span>;
              </p>
            </div>
          </div>

          <div
            ref={buttonsRef}
            className="flex flex-col sm:flex-row gap-3 md:gap-4 mb-6 md:mb-8 relative z-10"
          >
            <Button
              size="lg"
              className={`${commonStyles.background.primary} ${commonStyles.background.hover} text-white px-8 py-3 transform hover:scale-105 transition-all duration-300 shadow-lg hover:${commonStyles.shadow.primary}/25 font-semibold group relative overflow-hidden`}
              onClick={() => {
                const projectsSection = document.getElementById("projects");
                if (projectsSection) {
                  // Show success toast
                  toast({
                    variant: "success",
                    title: "🚀 Navigating to Portfolio",
                    description:
                      "Check out my latest projects and technical work!",
                  });

                  // Smooth scroll with slight delay for toast
                  setTimeout(() => {
                    projectsSection.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    });
                  }, 300);

                  // Add visual feedback
                  const button = document.activeElement as HTMLElement;
                  if (button) {
                    button.style.transform = "scale(0.95)";
                    setTimeout(() => {
                      button.style.transform = "";
                    }, 150);
                  }
                }
              }}
            >
              <span className="flex items-center gap-2 font-mono font-medium tracking-tight relative z-10">
                <Code2 className="w-5 h-5 group-hover:rotate-12 transition-transform" />
                View Portfolio
              </span>

              {/* Shimmer effect */}
              <div className="absolute inset-0 -top-[1px] -bottom-[1px] bg-gradient-to-r from-transparent via-white/20 to-transparent transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 ease-out" />
            </Button>
            <Button
              variant="outline"
              size="lg"
              className={`${heroStyles.button.outline} px-8 py-3 transform hover:scale-105 transition-all duration-300 font-semibold group`}
              onClick={() =>
                document
                  .getElementById("contact")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
            >
              <span className="flex items-center gap-2 font-mono font-medium tracking-tight">
                <Mail className="w-5 h-5 group-hover:rotate-12 transition-transform" />
                Contact Me
              </span>
            </Button>
          </div>

          <div
            ref={socialRef}
            className="flex gap-4 md:gap-6 relative z-10 mt-4 md:mt-0"
          >
            {[
              { icon: Github, href: "https://github.com/adarsh25-dev" },
              {
                icon: Linkedin,
                href: "https://www.linkedin.com/in/adarsh-parmar-161960288",
              },
              { icon: Mail, href: "mailto:adarshparmar.business@gmail.com" },
            ].map(({ icon: Icon, href }) => (
              <a
                key={href}
                href={href}
                className={`${heroStyles.social.color} ${commonStyles.text.hover} transition-all duration-300 transform hover:scale-125 hover:rotate-12`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Icon size={24} />
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
